import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import VoiceInputButton from '../VoiceInputButton';
import AnimatedPlaceholder from '../AnimatedPlaceholder';
import {
  askSendButtonBackground,
  askSendButtonShadow,
  heroBrandGlowShadow,
  type ThemeMode,
} from '../../theme/constants';

interface Props {
  onSubmit: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When true, renders a larger centered hero variant for the landing screen. */
  hero?: boolean;
  /** Pool of sample questions used by the animated placeholder (hero mode only). */
  animatedPlaceholders?: string[];
}

const MessageInput: React.FC<Props> = ({
  onSubmit,
  disabled = false,
  placeholder = 'Ask anything about your business...',
  hero = false,
  animatedPlaceholders,
}) => {
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState('');
  // Tracks whichever suggested question the animated placeholder is currently
  // rendering — lets the Accept button use the exact string the user sees.
  const [currentSuggestion, setCurrentSuggestion] = useState<string | null>(null);
  // Desktop pointer hover over the input row — freezes the animation so users
  // can read the whole question without it fading out mid-thought.
  const [inputHovered, setInputHovered] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    transcript,
    isListening,
    isSupported: speechSupported,
    currentLanguage,
    startListening,
    stopListening,
    resetTranscript,
    setLanguage,
  } = useSpeechToText({ continuous: false, interimResults: true });

  // Copy speech transcript into the input field
  useEffect(() => {
    if (transcript) {
      setValue(transcript);
      // Auto-size the textarea to fit new content
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
      }
    }
  }, [transcript]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setValue('');
    resetTranscript();
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const handleMicrophoneClick = () => {
    if (isListening) {
      stopListening();
    } else {
      resetTranscript();
      setValue('');
      startListening();
    }
  };

  // Fills the textarea with the currently-displayed suggested question and
  // focuses it so the user can submit immediately (Enter) or tweak first.
  // Cursor is placed at the end so typing appends rather than overwrites.
  const handleUseSuggestion = () => {
    if (!currentSuggestion || disabled) return;
    setValue(currentSuggestion);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    });
  };

  const showAnimatedPlaceholder =
    hero &&
    !isListening &&
    !focused &&
    value.length === 0 &&
    !!animatedPlaceholders &&
    animatedPlaceholders.length > 0;

  const staticPlaceholder = isListening
    ? 'Listening...'
    : showAnimatedPlaceholder
      ? ''
      : placeholder;

  const showUseSuggestion =
    showAnimatedPlaceholder && !!currentSuggestion && !disabled;

  return (
    <Wrapper $hero={hero}>
      <InputRow
        $isListening={isListening}
        $hero={hero}
        onMouseEnter={() => setInputHovered(true)}
        onMouseLeave={() => setInputHovered(false)}
      >
        <TextareaLayer $hero={hero}>
          <StyledTextarea
            ref={textareaRef}
            value={value}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={staticPlaceholder}
            disabled={disabled}
            rows={hero ? 2 : 1}
            $hero={hero}
          />
          {showAnimatedPlaceholder && (
            <AnimatedPlaceholder
              questions={animatedPlaceholders!}
              active={showAnimatedPlaceholder}
              paused={inputHovered}
              onQuestionChange={setCurrentSuggestion}
              $hero={hero}
            />
          )}
        </TextareaLayer>
        <Controls>
          {showUseSuggestion && (
            <UseSuggestionButton
              type="button"
              onClick={handleUseSuggestion}
              $hero={hero}
              $highlight={inputHovered}
              aria-label={`Use suggested question: ${currentSuggestion}`}
              title="Use this question"
            >
              <UseSuggestionIcon viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" $hero={hero}>
                {/* Corner-down-left: semantically "accept / pull into input". */}
                <path
                  d="M9 10L4 15L9 20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 4V11C20 12.0609 19.5786 13.0783 18.8284 13.8284C18.0783 14.5786 17.0609 15 16 15H4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </UseSuggestionIcon>
            </UseSuggestionButton>
          )}
          <VoiceInputButton
            currentLanguage={currentLanguage}
            onLanguageChange={setLanguage}
            isListening={isListening}
            onMicrophoneClick={handleMicrophoneClick}
            disabled={disabled}
            isSupported={speechSupported}
          />
          <SendButton
            onClick={handleSubmit}
            disabled={disabled || !value.trim()}
            aria-label="Send message"
            $hero={hero}
          >
            <SendIcon viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" $hero={hero}>
              <path
                d="M22 2L11 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22 2L15 22L11 13L2 9L22 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </SendIcon>
          </SendButton>
        </Controls>
      </InputRow>
    </Wrapper>
  );
};

export default MessageInput;

const Wrapper = styled.div<{ $hero?: boolean }>`
  padding: ${({ $hero }) => ($hero ? '0' : '10px 0 0')};
  border-top: ${({ theme, $hero }) => ($hero ? 'none' : `1px solid ${theme.av2.border}`)};
  background: ${({ theme, $hero }) => ($hero ? 'transparent' : theme.av2.pageBg)};
  width: 100%;
`;

const InputRow = styled.div<{ $isListening: boolean; $hero?: boolean }>`
  display: flex;
  align-items: ${({ $hero }) => ($hero ? 'flex-start' : 'center')};
  gap: ${({ $hero }) => ($hero ? '10px' : '8px')};
  background: ${({ theme, $hero }) =>
    $hero
      ? `linear-gradient(180deg, ${theme.av2.inputBgGradientStart} 0%, ${theme.av2.inputBgGradientEnd} 100%)`
      : theme.av2.surfaceElevated};
  border: 1px solid
    ${({ theme, $isListening, $hero }) =>
      $isListening ? theme.av2.error : $hero ? theme.av2.primaryBorder : theme.av2.border};
  border-radius: ${({ $hero }) => ($hero ? '16px' : '12px')};
  padding: ${({ $hero }) => ($hero ? '16px 18px' : '6px 8px 6px 14px')};
  transition: border-color 0.2s ease, box-shadow 0.25s ease;
  box-shadow: ${({ theme, $hero }) =>
    $hero ? heroBrandGlowShadow((theme.mode as ThemeMode) || 'light', 'rest') : 'none'};

  &:hover {
    ${({ theme, $hero, $isListening }) =>
      $hero && !$isListening
        ? `
          border-color: ${theme.av2.primary};
          box-shadow: ${heroBrandGlowShadow((theme.mode as ThemeMode) || 'light', 'hover')};
        `
        : ''}
  }

  &:focus-within {
    border-color: ${({ theme, $isListening }) => ($isListening ? theme.av2.error : theme.av2.primary)};
    box-shadow: 0 0 0 3px
        ${({ theme, $isListening }) =>
          $isListening ? 'rgba(214, 69, 69, 0.12)' : theme.av2.primaryFocusRing},
      ${({ theme, $hero }) =>
        $hero
          ? heroBrandGlowShadow((theme.mode as ThemeMode) || 'light', 'focus')
          : 'none'};
  }
`;

const TextareaLayer = styled.div<{ $hero?: boolean }>`
  position: relative;
  flex: 1;
  display: flex;
  min-width: 0;
  /* Reserve hero textarea for ~2 lines so the overall input height stays
     consistent whether the user has typed one line or two. */
  min-height: ${({ $hero }) => ($hero ? '52px' : 'auto')};
`;

const StyledTextarea = styled.textarea<{ $hero?: boolean }>`
  flex: 1;
  border: none;
  background: transparent;
  resize: none;
  outline: none;
  font-size: ${({ $hero }) => ($hero ? '15px' : '16px')};
  line-height: 1.55;
  color: ${({ theme }) => theme.av2.textPrimary};
  min-height: ${({ $hero }) => ($hero ? '52px' : '24px')};
  max-height: 200px;
  overflow-y: auto;
  position: relative;
  z-index: 1;
  padding: 0;
  font-family: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.av2.textLight};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const SendButton = styled.button<{ $hero?: boolean }>`
  flex-shrink: 0;
  width: ${({ $hero }) => ($hero ? '38px' : '34px')};
  height: ${({ $hero }) => ($hero ? '38px' : '34px')};
  border-radius: ${({ $hero }) => ($hero ? '10px' : '8px')};
  border: none;
  background: ${askSendButtonBackground};
  box-shadow: ${askSendButtonShadow};
  color: ${({ theme }) => theme.av2.textOnAccent};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: box-shadow 0.15s ease, opacity 0.15s ease, transform 0.15s ease,
    filter 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.06);
    transform: translateY(-1px);
    box-shadow:
      rgba(28, 136, 159, 0.55) 0px 0px 0px 2px,
      rgba(76, 80, 175, 0.45) 0px 3px 12px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SendIcon = styled.svg<{ $hero?: boolean }>`
  width: ${({ $hero }) => ($hero ? '18px' : '16px')};
  height: ${({ $hero }) => ($hero ? '18px' : '16px')};
`;

/**
 * "Use this question" accept button. Appears only while the animated placeholder
 * is active (hero mode, empty, not focused). On desktop, `$highlight` picks up
 * the hover-freeze state so the affordance visibly strengthens when the user
 * parks their pointer on the input. On touch the button is always visible at
 * its resting tone, ready to tap — no hover required.
 */
const UseSuggestionButton = styled.button<{ $hero?: boolean; $highlight: boolean }>`
  flex-shrink: 0;
  width: ${({ $hero }) => ($hero ? '34px' : '30px')};
  height: ${({ $hero }) => ($hero ? '34px' : '30px')};
  border-radius: ${({ $hero }) => ($hero ? '9px' : '7px')};
  border: 1px solid
    ${({ theme, $highlight }) => ($highlight ? theme.av2.primary : theme.av2.primaryBorder)};
  background: ${({ theme, $highlight }) => ($highlight ? theme.av2.primaryTint : theme.av2.primaryTintBg)};
  color: ${({ theme, $highlight }) => ($highlight ? theme.av2.primaryDark : theme.av2.accentText)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease,
    transform 120ms ease, box-shadow 150ms ease;
  box-shadow: ${({ theme, $highlight }) =>
    $highlight ? `0 2px 10px ${theme.av2.shadowAccent}` : 'none'};

  &:hover {
    border-color: ${({ theme }) => theme.av2.primary};
    background: ${({ theme }) => theme.av2.primaryTintStrong};
    color: ${({ theme }) => theme.av2.primaryDark};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.av2.primaryBorder};
    outline-offset: 2px;
  }
`;

const UseSuggestionIcon = styled.svg<{ $hero?: boolean }>`
  width: ${({ $hero }) => ($hero ? '16px' : '14px')};
  height: ${({ $hero }) => ($hero ? '16px' : '14px')};
`;

